import CryptoJS from 'crypto-js';
import fileDownload from 'js-file-download';
import moment from 'moment-timezone';
import { conferirServidor, respostaDeServidorFora } from 'utils/servidorFora';

const siteUrl = window.location.origin;
const isLocalIP = siteUrl.startsWith("http://192.168.168.1:9292");

export const baseURL = isLocalIP ? process.env.REACT_APP_BASE_URL_INTERNO : process.env.REACT_APP_BASE_URL_EXTERNO;
const PASS = process.env.REACT_APP_PASS


// Leituras da ficha do devedor e listas de apoio das abas: ao abrir a ficha, a tela principal, o cabeçalho e as abas
// (todas montadas de uma vez) pedem as mesmas listas ao mesmo tempo (o /devedor/by-id saía 4 vezes). A mesma leitura em
// andamento é aproveitada, cada um com a sua cópia, só se nenhuma gravação começou ou terminou depois que ela saiu: a
// recarga depois de salvar sempre vai ao servidor. Depois que a resposta chega, a próxima leitura vai ao servidor de novo.
const LEITURAS_COMPARTILHADAS = ["/devedor/by-id/", "/contratos/list/", "/acordo/list/", "/devedor/historico/", "/processos-transitorios/",
    "/dossie/list/", "/status-ficha/list-all", "/users/list-all-operators", "/marcacoes"];
const leiturasEmAndamento = new Map();
let versaoDasEscritas = 0;

function escrita(promessa) {
    versaoDasEscritas++;
    return promessa.finally(() => { versaoDasEscritas++; });
}

/**
 * Quantas gravações esta aba do navegador começou ou terminou até agora. A ficha do devedor compara para saber se a
 * alteração que achou no servidor foi feita daqui (salvar o cadastro, baixar...) ou por outro usuário (só aí avisa).
 */
export function escritasFeitas() {
    return versaoDasEscritas;
}

const SEM_RESPOSTA = "O sistema não respondeu (pode estar em manutenção ou sem conexão). Tente de novo em instantes.";

/**
 * fetch das chamadas ao backend. Resposta do proxy sem o backend (502/503/504) ou sem conexão faz a tela conferir se o
 * backend saiu do ar (utils/servidorFora.js: aviso "Sistema em manutenção"), e a falha de conexão chega à tela com uma
 * mensagem legível no lugar do "Failed to fetch".
 */
export function buscar(url, opcoes) {
    return fetch(url, opcoes).then(resposta => {
        if (respostaDeServidorFora(resposta.status)) conferirServidor();
        return resposta;
    }, erro => {
        if (erro?.name === "AbortError") throw erro;
        conferirServidor();
        throw new Error(SEM_RESPOSTA);
    });
}

/** Mensagem do 502/503/504 do proxy: a página de erro do IIS (HTML) não serve para a tela. */
function mensagemDoProxy(response) {
    return `O sistema não respondeu (HTTP ${response.status}): pode estar em manutenção ou a operação demorou demais. Tente de novo em instantes.`;
}

function copia(dados) {
    if (dados === null || typeof dados !== "object") return dados;
    return typeof window.structuredClone === "function" ? window.structuredClone(dados) : JSON.parse(JSON.stringify(dados));
}

function internalGet(url, headers) {
    const requestOptions = {
        method: 'GET',
        headers
    };
    return buscar(getUrl(url), requestOptions)
}

function internalPost(url, body, isJson, headers) {
    var requestOptions = {
        method: 'POST',
        headers: headers
    };
    if (body) {
        requestOptions.body = isJson ? JSON.stringify(body) : body;
    }
    // Listagem paginada (POST /<recurso>/list) só lê e o sinal de atividade da tela (POST /users/atividade, a cada 45 s)
    // não grava dado nenhum: não contam como gravação
    return /\/list([/?-]|$)/.test(url) || url.startsWith("/users/atividade")
        ? buscar(getUrl(url), requestOptions) : escrita(buscar(getUrl(url), requestOptions))
}

export async function get(url) {
    if (!LEITURAS_COMPARTILHADAS.some(prefixo => url.startsWith(prefixo))) {
        return internalGet(url, getHeaders(url))
            .then(handleJsonResponse)
    }
    const emAndamento = leiturasEmAndamento.get(url);
    if (emAndamento && emAndamento.versao === versaoDasEscritas) {
        return emAndamento.promessa.then(copia);
    }
    const promessa = internalGet(url, getHeaders(url)).then(handleJsonResponse);
    const leitura = { versao: versaoDasEscritas, promessa };
    leiturasEmAndamento.set(url, leitura);
    promessa.finally(() => {
        if (leiturasEmAndamento.get(url) === leitura) leiturasEmAndamento.delete(url);
    }).catch(() => {});
    return promessa.then(copia);
}

export async function post(url, body) {
    return internalPost(url, body, true, getHeaders(url))
        .then(handleJsonResponse)
}

export async function postFile(url, body, fileName) {
    return internalPost(url, body, true, getHeaders(url))
        .then(res => handleFileResponse(res, fileName))
}

function internalPut(url, body, isJson, headers) {
    var requestOptions = {
        method: 'PUT',
        headers: headers
    };
    if (body) {
        requestOptions.body = isJson ? JSON.stringify(body) : body;
    }
    return escrita(buscar(getUrl(url), requestOptions))
}

export async function put(url, body) {
    return internalPut(url, body, true, getHeaders(url))
        .then(handleJsonResponse)
}

export async function postNoResponse(url, body) {
    return internalPost(url, body, true, getHeaders(url))
        .then(async response => {
            isAuthenticate(response);
            if (!response.ok) {
                let errorMessage = `Erro HTTP ${response.status}`;

                if (respostaDeServidorFora(response.status)) {
                    errorMessage = mensagemDoProxy(response);
                } else if (response.headers.get('content-type')?.includes('application/json')) {
                    try {
                        const resJson = await response.json();
                        let parsed = resJson?.message || resJson?.customMessage;
                        if (!parsed && resJson?.error) {
                            parsed = typeof resJson.error === 'string' ? resJson.error : resJson.error?.name;
                        }
                        if (parsed) errorMessage = parsed;
                        console.error(resJson);
                    } catch (e) {}
                } else {
                    try {
                        const text = await response.text();
                        if (text && text.trim()) {
                            errorMessage = text.length > 200 ? text.substring(0, 200) + '...' : text;
                        }
                    } catch (e) {}
                }

                const error = new Error(errorMessage);
                error.status = response.status;
                throw error;
            }

            return response;
        });
}

function internalDelete(url, headers) {
    var requestOptions = {
        method: 'DELETE',
        headers: headers
    };
    return escrita(buscar(getUrl(url), requestOptions))
}

export async function del(url) {
    return internalDelete(url, getHeaders(url))
        .then(handleJsonResponse);
}

export async function delNoResponse(url) {
    return internalDelete(url, getHeaders(url))
        .then(async response => {
            isAuthenticate(response);
            if (!response.ok) {
                const error = new Error(await mensagemDeErro(response));
                error.status = response.status;
                throw error;
            }

            return response;
        });
}

export function wrappedGet(url) {
    return wrapPromise(get(url))
}

export function wrappedPost(url, body) {
    return wrapPromise(post(url, body))
}

export async function getFile(url, fileName) {
    return await internalGet(url, getHeadersGetFile(url))
        .then(res => handleFileResponse(res, fileName))
}

export async function getFileNoDownload(url) {
    return await internalGet(url, getHeaders(url))
        .then(res => handleFileResponseNoDownload(res))
}

export async function postZip(url, body, fileName) {
    return await internalPost(url, body, true, getHeaders(url))
        .then(res => handleFileResponse(res, fileName))
}


export async function postUploadNoResponse(url, formdata) {

    var requestOptions = {
        method: 'POST',
        headers: {
            'Authorization': sessionStorage.getItem('user')
        },
        body: formdata,
    };
    const response = await escrita(buscar(getUrl(url), requestOptions))

    if (response.ok) {
        return response
    } else {
        throw Error(await mensagemDeErro(response));
    }
}

export async function postUpload(url, formdata) {
    var requestOptions = {
        method: 'POST',
        headers: {
            'Authorization': sessionStorage.getItem('user')
        },
        body: formdata,
    };
    const response = await escrita(buscar(getUrl(url), requestOptions))

    if (response.ok) {
        return await response.json();
    } else {
        // O corpo do erro pode vir em JSON ({message}) ou em texto; nos dois casos a mensagem real chega à tela
        if (respostaDeServidorFora(response.status)) throw Error(mensagemDoProxy(response));
        let mensagem = `Erro HTTP ${response.status}`;
        const texto = await response.text().catch(() => "");
        try {
            const error = JSON.parse(texto);
            console.error(error)
            mensagem = error?.message || error?.customMessage || error?.error || mensagem;
        } catch (e) {
            if (texto && texto.trim()) mensagem = texto.length > 300 ? texto.substring(0, 300) + '...' : texto;
        }
        throw Error(mensagem);
    }
}

export async function postUploadFile(url, formdata, fileName) {
    var requestOptions = {
        method: 'POST',
        headers: {
            'Authorization': sessionStorage.getItem('user')
        },
        body: formdata,
    };
    const response = await escrita(buscar(getUrl(url), requestOptions));
    return handleFileResponse(response, fileName);
}




// Mensagem de erro da resposta: o corpo pode vir em JSON ({message}), em texto (página de erro do proxy) ou vazio
async function mensagemDeErro(response) {
    if (respostaDeServidorFora(response.status)) return mensagemDoProxy(response);
    let mensagem = `Erro HTTP ${response.status}`;
    const texto = await response.text().catch(() => "");
    try {
        const error = JSON.parse(texto);
        console.error(error)
        mensagem = error?.message || error?.customMessage || (typeof error?.error === 'string' ? error.error : null) || mensagem;
    } catch (e) {
        if (texto && texto.trim()) mensagem = texto.length > 300 ? texto.substring(0, 300) + '...' : texto;
    }
    return mensagem;
}

async function handleJsonResponse(response) {
    isAuthenticate(response)
    if (!response.ok) {
        throw Error(await mensagemDeErro(response));
    }
    // Endpoint sem retorno (void) responde 200 com corpo vazio: o response.json() falhava e a tela mostrava erro
    // com tudo já gravado (e o usuário salvava de novo, duplicando). Corpo que não é JSON volta como texto.
    const texto = await response.text();
    if (!texto) return null;
    try {
        return JSON.parse(texto);
    } catch (e) {
        return texto;
    }
}

const handleFileResponse = async (response, fileName) => {
    isAuthenticate(response)
    if (!response.ok) {
        // O corpo do erro pode vir em JSON ({message}), em texto ou vazio: a mensagem real chega à tela
        if (respostaDeServidorFora(response.status)) throw Error(mensagemDoProxy(response));
        let mensagem = `Erro HTTP ${response.status}`;
        const texto = await response.text().catch(() => "");
        try {
            const error = JSON.parse(texto);
            console.error(error)
            mensagem = error?.message || error?.customMessage || mensagem;
        } catch (e) {
            if (texto && texto.trim()) mensagem = texto.length > 300 ? texto.substring(0, 300) + '...' : texto;
        }
        throw Error(mensagem);
    }

    let actualFileName = fileName;
    if (!actualFileName) {
        const disposition = response.headers.get('content-disposition');
        if (disposition) {
            const starMatches = /filename\*=UTF-8''([^;=\n]*)/i.exec(disposition);
            if (starMatches && starMatches[1]) {
                try {
                    actualFileName = decodeURIComponent(starMatches[1]);
                } catch (e) {
                    actualFileName = starMatches[1];
                }
            } else {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(disposition);
                if (matches != null && matches[1]) { 
                    let rawName = matches[1].replace(/['"]/g, '');
                    try {
                        rawName = decodeURIComponent(rawName);
                    } catch (e) {}
                    try {
                        actualFileName = decodeURIComponent(escape(rawName));
                    } catch (e) {
                        actualFileName = rawName;
                    }
                }
            }
        }
    }
    if (!actualFileName) actualFileName = "download";

    // Arquivo salvo pelo servidor na pasta da Parametrização do Sistema > Pastas e Arquivos (PC, remessa e retorno CNAB,
    // Batimento, Comparador de Recortes): não baixa no computador, a tela mostra onde ficou (salvoEm). erroPasta = não
    // deu para salvar: o arquivo é baixado e a tela avisa o motivo.
    const salvoEm = cabecalhoTexto(response.headers, 'X-Arquivo-Salvo-Em');
    const erroPasta = cabecalhoTexto(response.headers, 'X-Arquivo-Erro-Pasta');
    if (!salvoEm) {
        const blob = await response.blob();
        fileDownload(blob, actualFileName)
    }

    // Quem precisa (ex.: Prestação de Contas, nº da PC) lê os cabeçalhos da resposta
    return { nome: actualFileName, headers: response.headers, salvoEm, erroPasta, baixado: !salvoEm };
}

/** Cabeçalho que o backend manda codificado como URL (ArquivoNaPasta), já decodificado; ausente = null. */
function cabecalhoTexto(headers, nome) {
    const valor = headers?.get(nome);
    if (!valor) return null;
    try {
        return decodeURIComponent(valor.replace(/\+/g, ' '));
    } catch (e) {
        return valor;
    }
}

const handleFileResponseNoDownload = async (response) => {
    isAuthenticate(response)
    if (!response.ok) {
        // O corpo do erro pode vir em JSON ({message}) ou em texto
        if (respostaDeServidorFora(response.status)) throw Error(mensagemDoProxy(response));
        let mensagem = `Erro HTTP ${response.status}`;
        const texto = await response.text().catch(() => "");
        try {
            const error = JSON.parse(texto);
            console.error(error)
            mensagem = error?.message || error?.customMessage || mensagem;
        } catch (e) {
            if (texto && texto.trim()) mensagem = texto.length > 300 ? texto.substring(0, 300) + '...' : texto;
        }
        throw Error(mensagem);
    }

    const blob = await response.blob();

    return blob;
}

function getUrl(url) {
    if (url.includes('http')) {
        return url;
    } else {
        return baseURL + url;
    }
}

export function getHeaders(url) {
    return {
        'Content-Type': 'application/json',
        'Authorization': url.includes("/public") ? getEncryptPublicAuth() : sessionStorage.getItem('user'),
        'Access-Control-Allow-Origin': '*'
    };
}

export function getHeadersGetFile(url) {
    return {
        'Content-Type': 'application/pdf',
        'Authorization': url.includes("/public") ? getEncryptPublicAuth() : sessionStorage.getItem('user'),
        'Access-Control-Allow-Origin': '*'
    };
}

function isAuthenticate(response) {
    if (response.status === 401 || response.status === 403) {
        cleanStorage();
        window.location.assign("/");
    }
}

export async function logoutSystem() {
    const token = sessionStorage.getItem('user');
    if (token) {
        try {
            await postNoResponse('/users/logout', {});
        } catch (e) {}
    }
    cleanStorage();
    localStorage.removeItem('selectedPages');
    sessionStorage.removeItem("redirectAfterLogin");
    window.location.assign('/');
}


export function cleanStorage() {
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('selectedPages');
}

export function getContext() {
    let url = window.location.href;
    let initIdx = url.indexOf("//") + 2;
    let lastIdx = url.indexOf(".");
    if (lastIdx < 0) {
        lastIdx = url.lastIndexOf(":");
    }
    url = url.substring(initIdx, lastIdx);
    return url;
}



export function wrapPromise(promise) {
    let status = 'pending';
    let response;

    const suspender = promise.then(
        res => {
            status = 'success';
            response = res;
        },
        err => {
            status = 'error';
            response = err;
        },
    );

    const handler = {
        pending: () => {
            throw suspender;
        },
        error: () => {
            return null;
        },
        default: () => response,
    };

    const read = () => {
        const result = handler[status] ? handler[status]() : handler.default();
        return result;
    };

    return { read };
}


export function getEncryptPublicAuth() {
    const date = new Date();
    return encrypt(moment.tz(date, "America/Sao_Paulo").format("yyyy-MM-DDTHH:mm:ss"));
}


function encrypt(value) {

    const key = generateKey(PASS, 32);
    const iv = generateKey(PASS, 16);

    const encryptedValue = CryptoJS.AES.encrypt(value, key, { iv: iv });
    const hex = hexEncode(encryptedValue.toString());

    return hex;
}

function hexEncode(value) {
    return CryptoJS.enc.Hex.stringify(CryptoJS.enc.Base64.parse(value))
}

function generateKey(name, length) {
    let key = name;
    if (!key)
        key = ""
    while (key.length < length) {
        key += "0";
    }
    return CryptoJS.enc.Utf8.parse(key);
}